import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useCondominio } from '../contexts/CondominioContext';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import { 
  FileText, Plus, Edit, Trash2, Check, X, Download, Upload,
  TrendingDown, TrendingUp, Calendar, DollarSign, Tag, User,
  Filter, Search, Paperclip
} from 'lucide-react';

const mesesDoAno = [
  { value: 1, label: 'Janeiro' },
  { value: 2, label: 'Fevereiro' },
  { value: 3, label: 'Março' },
  { value: 4, label: 'Abril' },
  { value: 5, label: 'Maio' },
  { value: 6, label: 'Junho' },
  { value: 7, label: 'Julho' },
  { value: 8, label: 'Agosto' },
  { value: 9, label: 'Setembro' },
  { value: 10, label: 'Outubro' },
  { value: 11, label: 'Novembro' },
  { value: 12, label: 'Dezembro' }
];

const NotasFiscais = () => {
  const { user } = useAuth();
  const { getCondominioId } = useCondominio();
  const [notasFiscais, setNotasFiscais] = useState([]);
  const [fornecedores, setFornecedores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingNF, setEditingNF] = useState(null);
  const [filtroTipo, setFiltroTipo] = useState('todos');
  const [filtroStatus, setFiltroStatus] = useState('todos');
  const [mesAno, setMesAno] = useState({
    mes: new Date().getMonth() + 1,
    ano: new Date().getFullYear()
  });
  const [arquivoSelecionado, setArquivoSelecionado] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  
  const [formData, setFormData] = useState({
    nf_tipo: 'entrada',
    nf_numero: '',
    nf_serie: '',
    nf_data_emissao: new Date().toISOString().split('T')[0],
    pessoa_id: '',
    nf_razao_social: '',
    nf_cnpj_cpf: '',
    nf_valor_produtos: '',
    nf_valor_desconto: '0',
    nf_valor_frete: '0',
    nf_valor_total: '',
    nf_categoria: '',
    nf_descricao: '',
    nf_status: 'pendente',
    condominio_id: getCondominioId()
  });

  const categorias = [
    'Água', 'Luz', 'Gás', 'Internet', 'Telefone',
    'Limpeza', 'Segurança', 'Manutenção', 'Jardinagem',
    'Salários', 'Encargos', 'Material de Limpeza',
    'Material de Escritório', 'Seguros', 'Taxas',
    'Outros'
  ];

  useEffect(() => {
    const condId = getCondominioId();
    if (condId) {
      loadNotasFiscais();
      loadFornecedores();
    }
  }, [mesAno, getCondominioId()]);

  const loadNotasFiscais = async () => {
    try {
      setLoading(true);
      const condominioId = getCondominioId();
      
      if (!condominioId) {
        console.log('Condomínio não selecionado');
        setNotasFiscais([]);
        setLoading(false);
        return;
      }
      
      console.log('📄 Carregando NFs do condomínio:', condominioId);
      
      const response = await api.get('/notas-fiscais', {
        params: {
          condominio_id: condominioId,
          mes: mesAno.mes,
          ano: mesAno.ano
        }
      });
      
      const nfs = response.data.data.notas_fiscais || [];
      console.log('📄 NFs recebidas:', nfs.length);
      
      // Debug: verificar se tem arquivos
      nfs.forEach((nf, index) => {
        if (nf.nf_arquivo_pdf || nf.nf_arquivo_xml) {
          console.log(`📎 NF ${index + 1} tem arquivo:`, nf.nf_arquivo_pdf || nf.nf_arquivo_xml);
        }
      });
      
      setNotasFiscais(nfs);
    } catch (error) {
      console.error('Erro ao carregar notas fiscais:', error);
      setNotasFiscais([]);
    } finally {
      setLoading(false);
    }
  };

  const loadFornecedores = async () => {
    try {
      const response = await api.get('/fornecedores', {
        params: { condominio_id: getCondominioId() }
      });
      setFornecedores(response.data.data.fornecedores || []);
    } catch (error) {
      console.error('Erro ao carregar fornecedores:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Preparar dados garantindo valores numéricos corretos
      const parseNumeric = (value) => {
        if (value === '' || value === null || value === undefined) return 0;
        const parsed = parseFloat(value);
        return isNaN(parsed) ? 0 : parsed;
      };
      
      const dados = {
        ...formData,
        nf_valor_produtos: parseNumeric(formData.nf_valor_total), // Usar valor total como base
        nf_valor_desconto: parseNumeric(formData.nf_valor_desconto),
        nf_valor_frete: parseNumeric(formData.nf_valor_frete),
        nf_valor_seguro: 0,
        nf_valor_outras_despesas: 0,
        nf_valor_total: parseNumeric(formData.nf_valor_total),
        nf_valor_icms: 0,
        nf_valor_ipi: 0,
        nf_valor_pis: 0,
        nf_valor_cofins: 0,
        pessoa_id: formData.pessoa_id || null,
        condominio_id: getCondominioId()
      };
      
      let nfSalva;
      if (editingNF) {
        const response = await api.put(`/notas-fiscais/${editingNF.nf_id || editingNF.id}`, dados);
        nfSalva = response.data.data.nota_fiscal;
      } else {
        const response = await api.post('/notas-fiscais', dados);
        nfSalva = response.data.data.nota_fiscal;
      }
      
      // Se há arquivo selecionado, fazer upload
      if (arquivoSelecionado && nfSalva) {
        await handleUploadComprovante(nfSalva.nf_id || nfSalva.id);
      }
      
      setShowModal(false);
      setEditingNF(null);
      resetForm();
      setArquivoSelecionado(null);
      loadNotasFiscais();
      
      if (window.showNotification) {
        window.showNotification('Nota fiscal salva com sucesso!', 'success');
      }
    } catch (error) {
      console.error('Erro ao salvar nota fiscal:', error);
      if (window.showNotification) {
        window.showNotification(error.response?.data?.message || 'Erro ao salvar', 'error');
      }
    }
  };

  const handleUploadComprovante = async (nfId) => {
    if (!arquivoSelecionado) return;
    
    try {
      setUploadingFile(true);
      const formData = new FormData();
      formData.append('arquivo', arquivoSelecionado);
      
      const response = await api.post(`/notas-fiscais/${nfId}/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      console.log('✅ Comprovante anexado com sucesso:', response.data);
      
      if (window.showNotification) {
        window.showNotification('Comprovante anexado com sucesso!', 'success');
      }
      
      // Recarregar a lista para mostrar o ícone de anexo
      await loadNotasFiscais();
      
    } catch (error) {
      console.error('Erro ao fazer upload:', error);
      if (window.showNotification) {
        window.showNotification('Erro ao anexar comprovante', 'error');
      }
    } finally {
      setUploadingFile(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validar tamanho (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        if (window.showNotification) {
          window.showNotification('Arquivo muito grande! Máximo 10MB', 'error');
        }
        return;
      }
      
      // Validar tipo
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
      if (!allowedTypes.includes(file.type)) {
        if (window.showNotification) {
          window.showNotification('Tipo de arquivo não permitido! Use PDF ou imagem', 'error');
        }
        return;
      }
      
      setArquivoSelecionado(file);
    }
  };

  const handleEdit = (nf) => {
    console.log('✏️ Editando NF:', nf.nf_id || nf.id);
    console.log('📎 Tem arquivo PDF?', nf.nf_arquivo_pdf ? 'SIM' : 'NÃO');
    console.log('📎 Tem arquivo XML?', nf.nf_arquivo_xml ? 'SIM' : 'NÃO');
    console.log('📎 Dados completos da NF:', nf);
    
    setEditingNF(nf);
    setFormData({
      nf_tipo: nf.nf_tipo || 'entrada',
      nf_numero: nf.nf_numero || '',
      nf_serie: nf.nf_serie || '',
      nf_data_emissao: nf.nf_data_emissao || '',
      pessoa_id: nf.pessoa_id || '',
      nf_razao_social: nf.nf_razao_social || '',
      nf_cnpj_cpf: nf.nf_cnpj_cpf || '',
      nf_valor_produtos: nf.nf_valor_produtos || '',
      nf_valor_desconto: nf.nf_valor_desconto || '0',
      nf_valor_frete: nf.nf_valor_frete || '0',
      nf_valor_total: nf.nf_valor_total || '',
      nf_categoria: nf.nf_categoria || '',
      nf_descricao: nf.nf_descricao || '',
      nf_status: nf.nf_status || 'pendente',
      condominio_id: getCondominioId()
    });
    setArquivoSelecionado(null);
    setShowModal(true);
  };

  const handleDownloadComprovante = async (nfId, nfData = null) => {
    try {
      // Buscar os dados da NF (pode vir do parâmetro ou da lista)
      const nfAtual = nfData || notasFiscais.find(nf => (nf.nf_id || nf.id) === nfId);
      const caminhoArquivo = nfAtual?.nf_arquivo_pdf || nfAtual?.nf_arquivo_xml;
      const nomeArquivo = caminhoArquivo ? caminhoArquivo.split('/').pop() : `comprovante-nf-${nfId}.pdf`;
      
      console.log('📥 NF encontrada:', nfAtual);
      console.log('📥 Caminho do arquivo:', caminhoArquivo);
      console.log('📥 Nome do arquivo a baixar:', nomeArquivo);
      
      const response = await api.get(`/notas-fiscais/${nfId}/comprovante`, {
        responseType: 'blob'
      });
      
      // Criar URL para download com nome correto preservando extensão
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = nomeArquivo; // Usar .download em vez de setAttribute
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
      
      console.log('✅ Download iniciado:', nomeArquivo);
      
      if (window.showNotification) {
        window.showNotification(`Download: ${nomeArquivo}`, 'success');
      }
    } catch (error) {
      console.error('Erro ao baixar comprovante:', error);
      if (window.showNotification) {
        window.showNotification('Erro ao baixar comprovante', 'error');
      }
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Tem certeza que deseja excluir esta nota fiscal?')) {
      try {
        await api.delete(`/notas-fiscais/${id}`);
        loadNotasFiscais();
        if (window.showNotification) {
          window.showNotification('Nota fiscal excluída!', 'success');
        }
      } catch (error) {
        console.error('Erro ao excluir:', error);
      }
    }
  };

  const handleAprovar = async (id) => {
    try {
      await api.put(`/notas-fiscais/${id}/aprovar`);
      loadNotasFiscais();
      if (window.showNotification) {
        window.showNotification('Nota fiscal aprovada!', 'success');
      }
    } catch (error) {
      console.error('Erro ao aprovar:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      nf_tipo: 'entrada',
      nf_numero: '',
      nf_serie: '',
      nf_data_emissao: new Date().toISOString().split('T')[0],
      pessoa_id: '',
      nf_razao_social: '',
      nf_cnpj_cpf: '',
      nf_valor_produtos: '',
      nf_valor_desconto: '0',
      nf_valor_frete: '0',
      nf_valor_total: '',
      nf_categoria: '',
      nf_descricao: '',
      nf_status: 'pendente',
      condominio_id: getCondominioId()
    });
    setArquivoSelecionado(null);
  };

  const calcularTotais = () => {
    const filtered = notasFiscais.filter(nf => {
      if (filtroTipo !== 'todos' && nf.nf_tipo !== filtroTipo) return false;
      if (filtroStatus !== 'todos' && nf.nf_status !== filtroStatus) return false;
      return true;
    });

    const entradas = filtered
      .filter(nf => nf.nf_tipo === 'entrada')
      .reduce((sum, nf) => sum + parseFloat(nf.nf_valor_total || 0), 0);
    
    const saidas = filtered
      .filter(nf => nf.nf_tipo === 'saida')
      .reduce((sum, nf) => sum + parseFloat(nf.nf_valor_total || 0), 0);

    return { entradas, saidas, saldo: entradas - saidas };
  };

  const totais = calcularTotais();
  const notasFiltradas = notasFiscais.filter(nf => {
    if (filtroTipo !== 'todos' && nf.nf_tipo !== filtroTipo) return false;
    if (filtroStatus !== 'todos' && nf.nf_status !== filtroStatus) return false;
    return true;
  });

  if (loading) return <LoadingSpinner />;

  return (
    <div className="p-6">
      {/* Header com Ícones */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <FileText className="h-8 w-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Notas Fiscais</h1>
            <p className="text-sm text-gray-500">Controle de Entrada e Saída</p>
          </div>
        </div>
        {['financeiro', 'administrador', 'sindico'].includes(user?.tipo) && (
          <button
            onClick={() => {
              setEditingNF(null);
              resetForm();
              setShowModal(true);
            }}
            className="btn btn-primary"
          >
            <Plus className="h-4 w-4 mr-2" />
            Nova Nota Fiscal
          </button>
        )}
      </div>

      {/* Cards de Resumo com Ícones */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-green-50 p-4 rounded-lg border border-green-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-green-600 font-medium">Entradas</p>
              <p className="text-2xl font-bold text-green-700">
                R$ {totais.entradas.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </p>
            </div>
            <TrendingUp className="h-8 w-8 text-green-600" />
          </div>
        </div>

        <div className="bg-red-50 p-4 rounded-lg border border-red-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-red-600 font-medium">Saídas</p>
              <p className="text-2xl font-bold text-red-700">
                R$ {totais.saidas.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </p>
            </div>
            <TrendingDown className="h-8 w-8 text-red-600" />
          </div>
        </div>

        <div className={`${totais.saldo >= 0 ? 'bg-blue-50 border-blue-200' : 'bg-orange-50 border-orange-200'} p-4 rounded-lg border`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm font-medium ${totais.saldo >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>Saldo</p>
              <p className={`text-2xl font-bold ${totais.saldo >= 0 ? 'text-blue-700' : 'text-orange-700'}`}>
                R$ {totais.saldo.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </p>
            </div>
            <DollarSign className={`h-8 w-8 ${totais.saldo >= 0 ? 'text-blue-600' : 'text-orange-600'}`} />
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="card">
        <div className="card-body">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Mês/Ano
            </label>
            <div className="flex gap-2">
              <select
                value={String(mesAno.mes)}
                onChange={(e) => setMesAno({ ...mesAno, mes: parseInt(e.target.value, 10) })}
                className="form-select flex-grow capitalize"
              >
                {mesesDoAno.map((mes) => (
                  <option key={mes.value} value={String(mes.value)}>
                    {mes.label}
                  </option>
                ))}
              </select>
              <input
                type="number"
                value={mesAno.ano}
                onChange={(e) => setMesAno({ ...mesAno, ano: parseInt(e.target.value) })}
                className="form-input w-20 text-center"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
              <Filter className="h-4 w-4" />
              Tipo
            </label>
            <select
              value={filtroTipo}
              onChange={(e) => setFiltroTipo(e.target.value)}
              className="form-select w-full"
            >
              <option value="todos">Todos</option>
              <option value="entrada">Entradas</option>
              <option value="saida">Saídas</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
              <Filter className="h-4 w-4" />
              Status
            </label>
            <select
              value={filtroStatus}
              onChange={(e) => setFiltroStatus(e.target.value)}
              className="form-select w-full"
            >
              <option value="todos">Todos</option>
              <option value="pendente">Pendentes</option>
              <option value="aprovada">Aprovadas</option>
              <option value="paga">Pagas</option>
            </select>
          </div>
        </div>
        </div>
      </div>

      {/* Lista de Notas Fiscais */}
      <div className="card">
        <div className="card-body">
          <div className="overflow-x-auto">
          <table className="w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Número</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fornecedor/Cliente</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Categoria</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Valor</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ações</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {notasFiltradas.map((nf) => (
                <tr key={nf.nf_id || nf.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${
                      nf.nf_tipo === 'entrada' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {nf.nf_tipo === 'entrada' ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                      {nf.nf_tipo === 'entrada' ? 'Entrada' : 'Saída'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">{nf.nf_numero}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {new Date(nf.nf_data_emissao).toLocaleDateString('pt-BR')}
                  </td>
                  <td className="px-6 py-4 text-sm">{nf.nf_razao_social || '-'}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {nf.nf_categoria && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">
                        <Tag className="h-3 w-3" />
                        {nf.nf_categoria}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    R$ {parseFloat(nf.nf_valor_total).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      nf.nf_status === 'aprovada' ? 'bg-green-100 text-green-800' :
                      nf.nf_status === 'paga' ? 'bg-blue-100 text-blue-800' :
                      nf.nf_status === 'pendente' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {nf.nf_status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <div className="flex items-center gap-2">
                      {(nf.nf_arquivo_pdf || nf.nf_arquivo_xml) && (
                        <button
                          onClick={() => handleDownloadComprovante(nf.nf_id || nf.id)}
                          className="p-1 text-purple-600 hover:bg-purple-100 rounded"
                          title="Baixar comprovante"
                        >
                          <Paperclip className="h-4 w-4" />
                        </button>
                      )}
                      <button
                        onClick={() => handleEdit(nf)}
                        className="p-1 text-blue-600 hover:bg-blue-100 rounded"
                        title="Editar"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      {nf.nf_status === 'pendente' && ['financeiro', 'administrador', 'sindico'].includes(user?.tipo) && (
                        <button
                          onClick={() => handleAprovar(nf.nf_id || nf.id)}
                          className="p-1 text-green-600 hover:bg-green-100 rounded"
                          title="Aprovar"
                        >
                          <Check className="h-4 w-4" />
                        </button>
                      )}
                      {['financeiro', 'administrador'].includes(user?.tipo) && (
                        <button
                          onClick={() => handleDelete(nf.nf_id || nf.id)}
                          className="p-1 text-red-600 hover:bg-red-100 rounded"
                          title="Excluir"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      </div>

      {/* Modal de Cadastro/Edição */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-container custom-scrollbar" style={{maxWidth: '800px'}}>
            <div className="modal-header">
              <h3 className="modal-title flex items-center gap-2">
                <FileText className="h-5 w-5" />
                {editingNF ? 'Editar Nota Fiscal' : 'Nova Nota Fiscal'}
              </h3>
              <button onClick={() => { setShowModal(false); resetForm(); }} className="modal-close-btn">
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6">
              {/* Tipo de NF */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de Nota Fiscal</label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="nf_tipo"
                      value="entrada"
                      checked={formData.nf_tipo === 'entrada'}
                      onChange={(e) => setFormData({ ...formData, nf_tipo: e.target.value })}
                      className="form-radio"
                    />
                    <TrendingUp className="h-4 w-4 text-green-600" />
                    <span>Entrada (Receita)</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="nf_tipo"
                      value="saida"
                      checked={formData.nf_tipo === 'saida'}
                      onChange={(e) => setFormData({ ...formData, nf_tipo: e.target.value })}
                      className="form-radio"
                    />
                    <TrendingDown className="h-4 w-4 text-red-600" />
                    <span>Saída (Despesa)</span>
                  </label>
                </div>
              </div>

              {/* Dados da NF */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="form-label form-label-required">Número da NF</label>
                  <input
                    type="text"
                    value={formData.nf_numero}
                    onChange={(e) => setFormData({ ...formData, nf_numero: e.target.value })}
                    required
                    className="form-input"
                    placeholder="Ex: 12345"
                  />
                </div>
                <div>
                  <label className="form-label">Série</label>
                  <input
                    type="text"
                    value={formData.nf_serie}
                    onChange={(e) => setFormData({ ...formData, nf_serie: e.target.value })}
                    className="form-input"
                    placeholder="Ex: 1"
                  />
                </div>
                <div>
                  <label className="form-label form-label-required">Data de Emissão</label>
                  <input
                    type="date"
                    value={formData.nf_data_emissao}
                    onChange={(e) => setFormData({ ...formData, nf_data_emissao: e.target.value })}
                    required
                    className="form-input"
                  />
                </div>
                <div>
                  <label className="form-label">Categoria</label>
                  <select
                    value={formData.nf_categoria}
                    onChange={(e) => setFormData({ ...formData, nf_categoria: e.target.value })}
                    className="form-select"
                  >
                    <option value="">Selecione...</option>
                    {categorias.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Fornecedor */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="form-label">Fornecedor Cadastrado</label>
                  <select
                    value={formData.pessoa_id}
                    onChange={(e) => setFormData({ ...formData, pessoa_id: e.target.value })}
                    className="form-select"
                  >
                    <option value="">Selecione...</option>
                    {fornecedores.map(f => (
                      <option key={f.pessoa_id || f.id} value={f.pessoa_id || f.id}>
                        {f.pessoa_nome || f.nome}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="form-label">Razão Social</label>
                  <input
                    type="text"
                    value={formData.nf_razao_social}
                    onChange={(e) => setFormData({ ...formData, nf_razao_social: e.target.value })}
                    className="form-input"
                  />
                </div>
              </div>

              {/* Valores */}
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="form-label form-label-required">Valor Total</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.nf_valor_total}
                    onChange={(e) => setFormData({ ...formData, nf_valor_total: e.target.value })}
                    required
                    className="form-input"
                    placeholder="0,00"
                  />
                </div>
                <div>
                  <label className="form-label">Desconto</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.nf_valor_desconto}
                    onChange={(e) => setFormData({ ...formData, nf_valor_desconto: e.target.value })}
                    className="form-input"
                    placeholder="0,00"
                  />
                </div>
                <div>
                  <label className="form-label">Frete</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.nf_valor_frete}
                    onChange={(e) => setFormData({ ...formData, nf_valor_frete: e.target.value })}
                    className="form-input"
                    placeholder="0,00"
                  />
                </div>
              </div>

              {/* Descrição */}
              <div className="mb-6">
                <label className="form-label">Descrição</label>
                <textarea
                  value={formData.nf_descricao}
                  onChange={(e) => setFormData({ ...formData, nf_descricao: e.target.value })}
                  rows="3"
                  className="form-textarea"
                  placeholder="Descreva os produtos/serviços..."
                />
              </div>

              {/* Upload de Comprovante */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                  <Upload className="h-4 w-4" />
                  Anexar Comprovante (PDF ou Imagem)
                </label>
                
                {/* Mostrar arquivo anexado */}
                {editingNF && (editingNF.nf_arquivo_pdf || editingNF.nf_arquivo_xml) && (
                  <div className="mb-3 p-2 bg-blue-50 border border-blue-200 rounded flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Paperclip className="h-4 w-4 text-blue-600" />
                      <span className="text-sm text-gray-700">
                        {editingNF.nf_arquivo_pdf 
                          ? editingNF.nf_arquivo_pdf.split('/').pop()
                          : editingNF.nf_arquivo_xml.split('/').pop()
                        }
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleDownloadComprovante(editingNF.nf_id || editingNF.id)}
                      className="flex items-center gap-1 px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-xs"
                    >
                      <Download className="h-3 w-3" />
                      Baixar
                    </button>
                  </div>
                )}
                
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.gif"
                  onChange={handleFileChange}
                  className="block w-full text-sm text-gray-500
                    file:mr-4 file:py-2 file:px-4
                    file:rounded-full file:border-0
                    file:text-sm file:font-semibold
                    file:bg-blue-50 file:text-blue-700
                    hover:file:bg-blue-100
                    cursor-pointer"
                />
                {arquivoSelecionado && (
                  <div className="mt-2 flex items-center gap-2 text-sm text-green-600">
                    <Check className="h-4 w-4" />
                    Novo arquivo: {arquivoSelecionado.name} ({(arquivoSelecionado.size / 1024).toFixed(2)} KB)
                  </div>
                )}
                {uploadingFile && (
                  <div className="mt-2 flex items-center gap-2 text-sm text-blue-600">
                    <span className="inline-block w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></span>
                    Enviando arquivo...
                  </div>
                )}
                <p className="mt-2 text-xs text-gray-500">
                  Formatos aceitos: PDF, JPG, PNG, GIF (máximo 10MB)
                </p>
              </div>

              {/* Botões */}
              <div className="form-actions">
                <button
                  type="button"
                  onClick={() => { setShowModal(false); resetForm(); }}
                  className="btn-base btn-secondary-custom"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="btn-base btn-primary-custom"
                >
                  {editingNF ? 'Atualizar' : 'Criar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotasFiscais;

